/**
 * One input field of a step and where its value comes from: a reference
 * picked from what is upstream, a fixed value, a list filter on a field that
 * takes a list (Feature 77 D8), or nothing (the capability's default
 * applies; a required field left so is refused on save).
 */
import { useState } from 'react';

import type { FieldSpec, LinkageSource } from '@optum/api-client/types';

import { Inline } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Select } from '../../ui/Select.js';
import { ListFilterEditor } from './ListFilterEditor.js';
import { itemFilter, itemPick } from './item-picks.js';
import { isListFilter, itemCondition } from './lists.js';
import { storedOption } from './positions.js';
import { PositionHint } from './PositionHint.js';
import {
  forPlace,
  PICKER_WIDTH,
  notOfferedWords,
  pickerOptions,
  type ReferenceOption,
} from './references.js';
import { ValueInput } from './ValueInput.js';

type SourceMode = 'ref' | 'fixed' | 'filter';

export function MappingRow({
  field,
  source,
  references,
  itemsOf,
  onChange,
}: {
  field: FieldSpec;
  source: LinkageSource | undefined;
  references: readonly ReferenceOption[];
  /** What a filter's item condition or pick may read, for the list at a path. */
  itemsOf: (ref: string) => ReferenceOption[];
  onChange: (source: LinkageSource | null) => void;
}) {
  const takesList = field.type === 'array' || field.type === 'any';
  // A filter emptied to whole items is `{ref}`, which reads as a reference;
  // the row stays on List filter until another mode is chosen.
  const [filtering, setFiltering] = useState(source !== undefined && isListFilter(source));
  const fixed = source !== undefined && 'value' in source;
  const mode: SourceMode = fixed ? 'fixed' : takesList && filtering ? 'filter' : 'ref';
  const ref = source !== undefined && 'ref' in source ? source.ref : '';
  // A list inside a list's items is never a filter's list (Feature 82 D12).
  const firstList =
    references.find((r) => !r.within && (r.type === 'array' || r.type === 'any'))?.value ?? '';
  const chooseMode = (next: SourceMode) => {
    setFiltering(next === 'filter');
    if (next === 'fixed') onChange({ value: null });
    else if (next === 'filter')
      onChange({ ref: ref || firstList, where: { join: 'all', items: [itemCondition()] } });
    else onChange(null);
  };
  // A reference the picker no longer offers (renamed, or no longer upstream) still shows.
  const known = ref === '' || references.some((r) => r.value === ref);
  const options = [
    { value: '', label: field.required ? 'not mapped (required)' : 'not mapped' },
    ...(known ? [] : [storedOption(ref, notOfferedWords(references, ref))]),
    // A list input offers a list's item fields; one value lists them disabled (D11).
    ...pickerOptions(forPlace(references, takesList ? 'list' : 'one')),
  ];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        paddingTop: 9,
        borderTop: '1px solid var(--border-divider)',
      }}
    >
      <Inline gap={7} align="center" justify="space-between" wrap={false}>
        <span className="mono" style={{ fontWeight: 'var(--weight-semibold)' }}>
          {field.name}
          {field.required && <span style={{ color: 'var(--danger)' }}> *</span>}
        </span>
        <SegmentedControl
          items={[
            { value: 'ref', label: 'Reference' },
            { value: 'fixed', label: 'Fixed' },
            ...(takesList ? [{ value: 'filter', label: 'List filter' }] : []),
          ]}
          value={mode}
          onChange={(next) => chooseMode(next as SourceMode)}
        />
      </Inline>
      {mode === 'filter' && source !== undefined && 'ref' in source ? (
        <ListFilterEditor
          field={field}
          source={source}
          references={references}
          itemsOf={itemsOf}
          onChange={onChange}
        />
      ) : fixed ? (
        <ValueInput
          label={`Value of ${field.name}`}
          value={source.value}
          onChange={(value) => onChange({ value })}
        />
      ) : (
        <Select
          searchable
          aria-label={`Source of ${field.name}`}
          value={ref}
          onChange={(value) => {
            const pick = itemPick(references, value);
            // A list input's pick of a list's item field builds a filter on that list (F82 D11).
            if (pick && takesList) {
              setFiltering(true);
              onChange(itemFilter(pick));
            } else onChange(value ? { ref: value } : null);
          }}
          panelWidth={PICKER_WIDTH}
          searchPlaceholder="Search a field…"
          options={options}
        />
      )}
      {mode === 'ref' && <PositionHint path={ref} />}
    </div>
  );
}
